import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import moment from 'moment';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import reorderArrayElement from 'common/util/reorderArrayElement';
import getTourListingsGoogleMapsDirectionsUrl from 'common/util/getTourListingsGoogleMapsDirectionsUrl';

import {
  TOUR_STATUS_SCHEDULED,
  TOUR_STATUS_CANCELLED,
  TOUR_SHOWING_STATUS_UNREAD,
  TOUR_SHOWING_STATUS_CONFIRMED,
  TOUR_SHOWING_STATUS_FAILED,
} from 'common/tourConstants';

import {
  LISTING_DATA_SOURCE_SMART_APARTMENT,
} from 'common/listingConstants';

import {
  SR_API_ACTION_ADMIN_UPDATE_SHOWING_POSITIONS,
  SR_API_ACTION_APPLY_TOUR_STATE_CHANGE,
  SR_API_ACTION_ADMIN_UNSCHEDULE_SHOWING,
  SR_API_ACTION_CALCULATE_TOUR_ROUTE,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_ADMIN_TOURS,
} from 'common/dataTableConstants';

import {
  getApplicationMovePeriodFormat,
} from 'common/householdApplication/applicationHelpers';
import {
  getOccupancyCellContentFromListing,
  isTourUserPrequalCompleted,
  getShowingDisqualifications,
} from 'client/components/SrAdminTourCard/tourCardHelpers';
import SrLink from 'client/components/SrLink/SrLink';
import SrTourRescheduleLink from 'client/components/SrTourRescheduleLink';
import SrTourCopyMlsNumber from 'client/components/SrTourCopyMlsNumber';
import SrTourCopyAllMlsNumbersHeader from 'client/components/SrTourCopyAllMlsNumbersHeader';
import SrTourCopyAllAddressesHeader from 'client/components/SrTourCopyAllAddressesHeader';
import SrAdminTourCardDeleteDropdown from 'client/components/SrAdminTourCard/SrAdminTourCardDeleteDropdown';
import SrAdminTourNotes from 'client/components/SrAdminTourCard/SrAdminTourNotes';
import SrAdminTourCardSelectTourAgent from 'client/components/SrAdminTourCard/SrAdminTourCardSelectTourAgent';
import SrAdminTourCardSelectStartDate from 'client/components/SrAdminTourCard/SrAdminTourCardSelectStartDate';
import SrAdminTourCardRoute from 'client/components/SrAdminTourCard/SrAdminTourCardRoute';
import SrAdminTourCardListingCell from 'client/components/SrAdminTourCard/SrAdminTourCardListingCell';
import SrAssignHouseholdAdminSelect from 'client/components/SrAssignHouseholdAdminSelect';
import SrHouseholdNotesContainer from 'client/components/SrHouseholdNotesContainer';
import SrBadge from 'client/components/SrBadge';
import SrCopyText from 'client/components/SrCopyText';
import SrFrontConversation from 'client/components/SrFrontConversation';

import postWithState from 'client/actions/postWithState';
import checkIfIdleApiState from 'client/helpers/checkIfIdleApiState';
import {
  actionDataTableRemoveItem,
} from 'client/helpers/dataTables';
import TourActions from 'client/actions/Tour';
import withApiState from 'client/helpers/withApiState';

import branch from 'client/core/branch';

@withApiState((props) => ({
  calculateTourRouteApiState: {
    action: SR_API_ACTION_CALCULATE_TOUR_ROUTE,
    queryId: `${props.tour.id}`,
  },
}))
@branch({
  tourAgents: ['tourAgents'],
  apiState: ['apiState'],
  householdAdminsList: ['householdAdminsList'],
})
class SrAdminTourCardRequested extends Component {
  static propTypes = {
    tour: PropTypes.object.isRequired,
    tourAgents: PropTypes.array.isRequired,
    reloadDataTableTour: PropTypes.func.isRequired,
    onMakeAvailableShowingButtonClick: PropTypes.func.isRequired,
    onNotAvailableShowingButtonClick: PropTypes.func.isRequired,
    onShowingMarkReadButtonClick: PropTypes.func.isRequired,
    onOpenShowingNotes: PropTypes.func.isRequired,
    onOpenMessageTenant: PropTypes.func.isRequired,
    onOpenRenterPrequal: PropTypes.func.isRequired,
    apiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    appUrl: PropTypes.string.isRequired,
    calculateTourRouteApiState: PropTypes.object.isRequired,
    householdAdminsList: PropTypes.array,
  }

  constructor(props) {
    super(props);

    this.state = {
      tourRoute: null,
    };
  }

  _onShowingCompletedButtonClick = ({ listingId }) => {
    const { tour, reloadDataTableTour, dispatch } = this.props;

    dispatch(TourActions.updateShowingStatus, {
      tourId: tour.id,
      listingId: listingId,
      showingStatus: TOUR_SHOWING_STATUS_CONFIRMED,
    }).then(reloadDataTableTour);
  }

  _onCreateNewTourButtonClick = ({ listingId }) => {
    if (!global.confirm('Are you sure you want to split this showing off into a new tour?')) {
      return;
    }
    const { tour, reloadDataTableTour, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UNSCHEDULE_SHOWING,
      queryId: `${tour.id}-${listingId}`,
      payload: {
        tourId: tour.id,
        listingId: listingId,
      },
    }).then((apiRequestShowingResponsePayload) => {
      // (Re)load tour holding the newly requested showing, created by un-scheduling this showing.
      return reloadDataTableTour({
        tourIdToLoad: apiRequestShowingResponsePayload.requestShowingResult.id,
      });
    }).then(() => {
      if (tour.listings.length > 1) {
        return reloadDataTableTour();
      }

      return dispatch(actionDataTableRemoveItem, {
        dataSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
        entityId: tour.id,
      });
    });
  }

  _onShowingAddToTourButtonClick = ({ listingId }) => {
    const {
      tour,
      addToTour,
    } = this.props;

    addToTour({
      listingId,
      tour,
    });
  }

  _onScheduleButtonClick = () => {
    const { tour, reloadDataTableTour, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_APPLY_TOUR_STATE_CHANGE,
      payload: {
        entityId: tour.id,
        toState: TOUR_STATUS_SCHEDULED,
      },
    }).then(reloadDataTableTour);
  }

  _onDragEnd = (dragResult) => {
    if (!dragResult.source || !dragResult.destination) {
      return;
    }

    const { tour, reloadDataTableTour, dispatch } = this.props;
    const initialIndex = dragResult.source.index;
    const newIndex = dragResult.destination.index;

    if (initialIndex !== newIndex) {
      const reorderedListings = reorderArrayElement(tour.listings, initialIndex, newIndex);
      const updatedShowingPositions = reorderedListings.reduce((acc, listing, index) => ({
        ...acc,
        [listing.id]: index,
      }), {});

      dispatch(postWithState, {
        action: SR_API_ACTION_ADMIN_UPDATE_SHOWING_POSITIONS,
        queryId: `${tour.id}`,
        payload: {
          tourId: tour.id,
          updatedShowingPositions: updatedShowingPositions,
        },
      }).then(reloadDataTableTour);
    }
  }

  _calculateTourRoute = () => {
    const { tour, dispatch, reloadDataTableTour } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_CALCULATE_TOUR_ROUTE,
      queryId: `${tour.id}`,
      payload: {
        tourId: tour.id,
      },
    }).then((tourRoute) => {
      this.setState({ tourRoute });
      return reloadDataTableTour();
    });
  }

  _onHideTourRouteClick = () => {
    this.setState({ tourRoute: null });
  }

  render() {
    const {
      tour,
      tourAgents,
      reloadDataTableTour,
      onSaveForLaterButtonClick,
      onMakeAvailableShowingButtonClick,
      onNotAvailableShowingButtonClick,
      onShowingMarkReadButtonClick,
      onOpenShowingNotes,
      onOpenMessageTenant,
      onOpenRenterPrequal,
      apiState,
      dispatch,
      appUrl,
      calculateTourRouteApiState,
      householdAdminsList,
    } = this.props;

    const {
      tourRoute,
    } = this.state;

    const {
      user,
      renterProfile,
    } = tour;

    const isPrequalified = isTourUserPrequalCompleted(tour);
    const userFrontConversation = tour.userFrontConversations.find(({ userId }) => userId === user.id);

    const householdAssignedAdminUserId = tour.household && tour.household.assignedAdminUserId;
    const apiStateIsIdle = checkIfIdleApiState(apiState);
    const nowMomentWithUtcOffset = momentWithAustinTimezone();
    const submitButtonIsDisabled = (
      !apiStateIsIdle
      || tour.showings.some(({ status }) => ![
        TOUR_SHOWING_STATUS_CONFIRMED,
        TOUR_SHOWING_STATUS_FAILED,
      ].includes(status))
      || !tour.startDate
      || momentWithAustinTimezone(tour.startDate).hours() < 7
      || momentWithAustinTimezone(tour.startDate).hours() >= 22
      || momentWithAustinTimezone(tour.startDate).isBefore(momentWithAustinTimezone())
      || !!tour.listings.find((listing) => {
        const showing = tour.showings.find(({ listingId }) => listingId === listing.id);
        return (
          getShowingDisqualifications({ tour, listing }).isDisqualified
          && showing.status !== TOUR_SHOWING_STATUS_FAILED
        );
      })
      || !tour.showings.find((showing) => showing.status === TOUR_SHOWING_STATUS_CONFIRMED)
    );
    const googleMapsDirectionsUrl = getTourListingsGoogleMapsDirectionsUrl({
      listings: tour.listings,
      showings: tour.showings,
    });

    return (
      <div className={classnames(`tourCard requested renter-${user.phone}`, {
        needsRescheduling: tour.markedNeedsReschedulingFlag,
        archived: tour.status === TOUR_STATUS_CANCELLED,
      })}
      >
        {/* Colored Banners for special tour states */}
        {tour.status === TOUR_STATUS_CANCELLED && (
          <div className='tourBanner archived'>
            <span className='label'>Archived</span>
          </div>
        )}
        {tour.markedNeedsReschedulingFlag && (
          <div className='tourBanner needsRescheduling'>
            <span className='label'>Needs Rescheduling</span>
          </div>
        )}
        {/* Top info bar */}
        <div className='topInfo'>
          <div>
            <div>
              <span className='renterNameInfo'>
                <span
                  onClick={() => onOpenRenterPrequal(user.id)}
                  className={classnames('renterName', {
                    isPrequalified: isPrequalified,
                  })}
                >
                  {user.name}
                  {tour.scheduledForAutotext
                    && momentWithAustinTimezone(tour.textedAtDate).isAfter(nowMomentWithUtcOffset)
                    && (
                      <span> (Auto-texting at 8:30AM)</span>
                    )}
                </span>
              </span>
              <SrBadge>{tour.market && tour.market.displayName}</SrBadge>
            </div>
            <div className='subhead'>
              {userFrontConversation && (
                <SrFrontConversation
                  frontConversation={userFrontConversation}
                  user={user}
                  dispatch={dispatch}
                />
              )}
              {userFrontConversation && (
                <SrLink
                  className='linkOut front'
                  target='_blank'
                  to={`https://app.frontapp.com/open/${userFrontConversation.conversationId}`}
                >
                  Front
                </SrLink>
              )}
              {googleMapsDirectionsUrl && (
                <SrLink
                  className='linkOut gmaps'
                  target='_blank'
                  to={googleMapsDirectionsUrl}
                >
                  Maps
                </SrLink>
              )}
              <SrLink
                className='linkOut household'
                target='_blank'
                to={`${appUrl}/admin/household/all/${user.id}`}
              >
                Household
              </SrLink>
              <span
                className='linkOut automate'
                onClick={this._calculateTourRoute}
              >
                Automate
              </span>
              <SrLink
                className='linkOut addToTour'
                target='_blank'
                to={`${appUrl}/admin/tours/${tour.id}/add-to-tour`}
              >
                Add
              </SrLink>
              <div className='subhead'>
                <span className='detail'>
                  {renterProfile
                    ? getApplicationMovePeriodFormat(renterProfile)
                    : `${momentWithAustinTimezone(tour.renterMoveInDate).format('MM/DD/YYYY')} Move-in Date`
                  }
                </span>
                <span className='detail additional'>
                  <SrCopyText
                    formattedText={`Phone ${user.phone}`}
                    text={user.phone}
                  />
                </span>
                <span className='detail additional'>
                  {tour.chosenDates.map((timeDate, i) => {
                    const format = i === 0 ? 'MMM Do: h:mma' : 'h:mma';
                    return momentWithAustinTimezone(timeDate).format(format);
                  }).join(', ')}
                </span>
              </div>
            </div>
          </div>
          <div className='timestampContainer'>
            <span className='timestamp'>
              {tour.markedNeedsReschedulingFlag && tour.markedNeedsReschedulingDate ? (
                `Marked as Needs Rescheduling at ${momentWithAustinTimezone(tour.markedNeedsReschedulingDate).format('h:mmA [on] MM/DD/YYYY')}` // eslint-disable-line max-len
              ) : (
                tour.textedAtDate &&
                  momentWithAustinTimezone(tour.textedAtDate).isBefore(nowMomentWithUtcOffset)
                  ? (
                    `Texted at ${momentWithAustinTimezone(tour.textedAtDate).format('h:mmA [on] MM/DD/YYYY')}` // eslint-disable-line max-len
                  )
                  : (
                    `Requested at ${momentWithAustinTimezone(tour.createdDate).format('h:mmA [on] MM/DD/YYYY')}` // eslint-disable-line max-len
                  )
              )}
            </span>
          </div>
        </div>

        {calculateTourRouteApiState.inProgress && (
          <div className='adminLoadingGif' />
        )}

        {!calculateTourRouteApiState.inProgress && tourRoute && (
          <SrAdminTourCardRoute {...tourRoute} onClose={this._onHideTourRouteClick} />
        )}

        {/* Table listing info section */}
        <div className='listingTable'>
          <DragDropContext onDragEnd={this._onDragEnd}>
            <table>
              <thead>
                <tr>
                  <th className='listing'>
                    <SrTourCopyAllAddressesHeader
                      user={user}
                      listings={tour.listings}
                      showings={tour.showings}
                    />
                  </th>
                  <th className='status'>status</th>
                  <th className='availableDate'>availability</th>
                  <th className='occupancy'>occupancy</th>
                  <th className='mls'>
                    <SrTourCopyAllMlsNumbersHeader
                      listings={tour.listings}
                      showings={tour.showings}
                    />
                  </th>
                  <th className='notes'>notes</th>
                  <th className='actions'>listing actions</th>
                </tr>
              </thead>
              <Droppable
                droppableId={`droppable-${tour.id}`}
                direction='vertical'
              >
                {(dropProvided) => (
                  <tbody
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                  >
                    {tour.listings.map((listing, index) => {
                      const showing = tour.showings.find(({ listingId }) => listingId === listing.id);
                      const showingDisqualifications = getShowingDisqualifications({ tour, listing });
                      return (
                        <tr
                          key={listing.id}
                          className={classnames({
                            notAvailable: showing.status === TOUR_SHOWING_STATUS_FAILED,
                            unread: showing.status === TOUR_SHOWING_STATUS_UNREAD,
                            disqualified: (
                              showing.status !== TOUR_SHOWING_STATUS_UNREAD
                              && showingDisqualifications.isDisqualified
                            ),
                          })}
                        >
                          <Draggable
                            draggableId={`draggable-tour-${tour.id}-listing-${listing.id}`}
                            index={index}
                          >
                            {(dragProvided) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                              >
                                <SrAdminTourCardListingCell listing={listing} showing={showing} />
                                <td className='status'>
                                  {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                                    ? listing.status
                                    : listing.mlsStatus
                                  }
                                </td>
                                <td className='availableDate'>
                                  {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                                    ? (
                                      <SrLink
                                        to={
                                          'https://app.smartapartmentdata.com/ViewProperty.aspx' +
                                          `?propertyID=${encodeURIComponent(listing.smartApartmentId)}`
                                        }
                                        target='_blank'
                                      >
                                        View Floorplans
                                      </SrLink>
                                    )
                                    : listing.availableDate
                                      ? moment.utc(listing.availableDate).format('MMM Do, YYYY')
                                      : '--'
                                  }
                                </td>
                                <td className='occupancy'>
                                  {getOccupancyCellContentFromListing(listing) || '--'}
                                </td>
                                <td className='mls'>
                                  {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                                    ? ('Smart')
                                    : (
                                      <SrTourCopyMlsNumber mlsNumber={listing.mlsNumber} />
                                    ) || '--'
                                  }
                                </td>
                                <SrAdminTourNotes
                                  showing={showing}
                                  listing={listing}
                                  onOpenShowingNotes={onOpenShowingNotes}
                                />
                                <td className='actions'>
                                  <div
                                    className={classnames(
                                      'listingActionButton',
                                      {
                                        confirmed: showing.status === TOUR_SHOWING_STATUS_CONFIRMED,
                                        notAvailable: showing.status === TOUR_SHOWING_STATUS_FAILED,
                                      }
                                    )}
                                  >
                                    {showing.status === TOUR_SHOWING_STATUS_UNREAD ? (
                                      <div className='confirmContainer'>
                                        <span
                                          className='mainAction unread'
                                          onClick={() => {
                                            onShowingMarkReadButtonClick({
                                              listingId: listing.id,
                                            });
                                          }}
                                        >
                                          mark as read
                                        </span>
                                      </div>
                                    ) : (
                                      <div className='confirmContainer'>
                                        <div className='arrowDropDown' />
                                        {(showing.status === TOUR_SHOWING_STATUS_CONFIRMED
                                          ? (<span className='mainAction'>confirmed</span>)
                                          : (showing.status === TOUR_SHOWING_STATUS_FAILED
                                            ? (
                                              <span className='mainAction'>
                                                not available
                                                {showing.note && (
                                                  <span className='tooltip'>
                                                    {showing.note}
                                                  </span>
                                                )}
                                              </span>
                                            )
                                            : (
                                              <span
                                                className='mainAction'
                                                onClick={() => {
                                                  this._onShowingCompletedButtonClick({
                                                    listingId: listing.id,
                                                  });
                                                }}
                                              >
                                                confirm
                                              </span>
                                            )
                                          )
                                        )}
                                        <div className='dropdown'>
                                          <span
                                            className='option'
                                            onClick={() => onOpenMessageTenant(listing.id)}
                                          >
                                            message tenant
                                          </span>
                                          {tour.status === TOUR_STATUS_CANCELLED && (
                                            <span
                                              className='option reschedule'
                                              onClick={() => this._onCreateNewTourButtonClick({
                                                listingId: listing.id,
                                              })}
                                            >
                                              create new tour
                                            </span>
                                          )}
                                          <span
                                            className='option addToTour'
                                            onClick={() => this._onShowingAddToTourButtonClick({
                                              listingId: listing.id,
                                            })}
                                          >
                                            add to tour
                                          </span>
                                          {showing.status === TOUR_SHOWING_STATUS_FAILED
                                            ? (
                                              <span
                                                className='option delete'
                                                onClick={() => onMakeAvailableShowingButtonClick({
                                                  listingId: listing.id,
                                                })}
                                              >
                                                make available
                                              </span>
                                            ) : (
                                              <span
                                                className='option delete'
                                                onClick={() => onNotAvailableShowingButtonClick({
                                                  listingId: listing.id,
                                                })}
                                              >
                                                not available
                                              </span>
                                            )}
                                          <span
                                            className='option saveForLater'
                                            onClick={() => onSaveForLaterButtonClick({
                                              listing: listing,
                                            })}
                                          >
                                            save for later
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                {showing.status !== TOUR_SHOWING_STATUS_UNREAD
                                  && showingDisqualifications.disqualifications.length > 0
                                  && (
                                    <div className='showingDisqualifications'>
                                      <div className='showingDisqualificationsTitle'>
                                        <span>
                                          Disqualifications:
                                        </span>
                                      </div>
                                      <ul>
                                        {showingDisqualifications.disqualifications
                                          .map((disqualification) => {
                                            return (
                                              <li
                                                className='showingDisqualificationsRow'
                                                key={`${listing.id}-${disqualification}`}
                                              >
                                                <span>{disqualification}</span>
                                              </li>
                                            );
                                          })
                                        }
                                      </ul>
                                    </div>
                                  )}
                              </div>
                            )}
                          </Draggable>
                        </tr>
                      );
                    })}
                    {dropProvided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </table>
          </DragDropContext>
        </div>

        <div className='notesContainer'>
          <span className='notesTitle'>
            notes
          </span>

          <SrHouseholdNotesContainer
            household={tour.household}
            reloadDataTable={reloadDataTableTour}
          />
        </div>

        {/* Footer actions */}
        <div className='bottomActions clearfix'>
          <div className='mainActionsContainer'>
            {tour.status !== TOUR_STATUS_CANCELLED && (
              <div className='deleteContainer'>
                <span className='markDead'>Archive</span>
                <SrAdminTourCardDeleteDropdown
                  tour={tour}
                  reloadDataTableTour={reloadDataTableTour}
                  dispatch={dispatch}
                />
              </div>
            )}
            <button
              className={classnames(
                'button blue mainAction',
                submitButtonIsDisabled && 'disabled',
              )}
              type='button'
              disabled={submitButtonIsDisabled}
              onClick={this._onScheduleButtonClick}
            >
              {apiStateIsIdle ? 'schedule' : 'loading...'}
            </button>
            <SrAdminTourCardSelectStartDate
              tour={tour}
              reloadDataTableTour={reloadDataTableTour}
              dispatch={dispatch}
            />
            <SrAdminTourCardSelectTourAgent
              tour={tour}
              tourAgents={tourAgents}
              reloadDataTableTour={reloadDataTableTour}
              dispatch={dispatch}
            />
            <SrTourRescheduleLink
              tour={tour}
              listingId={tour.listings[0].id}
              reloadDataTableTour={reloadDataTableTour}
            />
          </div>
          {tour.household
            && householdAdminsList
            && (
              <div className='assignedAdminContainer'>
                <SrAssignHouseholdAdminSelect
                  householdId={tour.household.id}
                  assignedAdminUserId={householdAssignedAdminUserId}
                  reloadDataTable={reloadDataTableTour}
                  householdAdminsList={householdAdminsList}
                  dispatch={dispatch}
                />
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

SrAdminTourCardRequested.displayName = 'SrAdminTourCardRequested';

export default SrAdminTourCardRequested;
